import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function FinalCTA() {
  const { t } = useTranslation();

  const trustItems = [
    { icon: 'ri-shield-check-line', key: 'cta.trust1' },
    { icon: 'ri-time-line', key: 'cta.trust2' },
    { icon: 'ri-flashlight-line', key: 'cta.trust3' },
  ];

  return (
    <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-red-50 via-white to-red-50/50">
      {/* Subtle animated gradient orbs */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-red-100/50 blur-[120px]"
        animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      ></motion.div>

      <motion.div
        className="absolute top-1/4 right-1/4 w-[300px] h-[300px] rounded-full bg-amber-50 blur-[100px]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      ></motion.div>

      <div className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: 'radial-gradient(circle, oklch(var(--primary-500)/0.3) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      ></div>

      <div className="relative z-10 mx-auto max-w-[800px] px-6 md:px-10 text-center">
        <ScrollReveal direction="up" duration={0.7}>
          <h2 className="font-heading text-3xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {t('cta.title')}{' '}
            <span className="text-gradient-red">{t('cta.titleHighlight')}{t('cta.titleEnd')}</span>
          </h2>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={0.15} duration={0.7}>
          <p className="text-gray-600 text-base md:text-lg max-w-xl mx-auto mb-10 leading-relaxed">
            {t('cta.subtitle')}
          </p>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={0.3} duration={0.6}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.a
              href="#contact"
              className="px-8 py-4 rounded-full bg-primary-500 text-white font-semibold text-sm md:text-base hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              {t('cta.button1')}
              <i className="ri-arrow-right-line ml-2"></i>
            </motion.a>
            <motion.a
              href="https://wa.me/573000000000"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 rounded-full border border-red-200 text-gray-800 font-medium text-sm md:text-base hover:bg-red-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              <i className="ri-whatsapp-line mr-2"></i>
              {t('cta.button2')}
            </motion.a>
          </div>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={0.5} duration={0.7}>
          <div className="mt-16 flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {trustItems.map((item) => (
              <motion.div
                key={item.key}
                className="flex items-center gap-2 text-gray-500"
                whileHover={{ scale: 1.05 }}
              >
                <i className={`${item.icon} text-red-500 text-lg`}></i>
                <span className="text-xs md:text-sm">{t(item.key)}</span>
              </motion.div>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}